import React, { useEffect, useState } from "react";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  OfficeBuildingIcon,
  StarIcon,
} from "@heroicons/react/solid";
import { useDispatch, useSelector } from "react-redux";
import { getBranchesThunk } from "../../store/thunks/branchThunks";

const BranchHierarchyTree = ({ mainBranch, onSelectBranch, onSetMain }) => {
  const dispatch = useDispatch();
  const [expandedNodes, setExpandedNodes] = useState({});

  const { branches, loadingList, filters, totalPages, currentPage } =
    useSelector((state) => state.branch);

  useEffect(() => {
    dispatch(getBranchesThunk({ ...filters, page: currentPage }));
  }, [dispatch, filters, currentPage]);

  // Organize branches by state
  const organizedBranches = branches.reduce((acc, branch) => {
    const state = branch.stateCode || "UNASSIGNED";
    if (!acc[state]) acc[state] = [];
    acc[state].push(branch);
    return acc;
  }, {});

  const toggleExpand = (nodeId) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeId]: !prev[nodeId],
    }));
  };

  const getBranchColor = (branch) => {
    if (branch.code === mainBranch) return "text-yellow-600 bg-yellow-50";
    if (branch.rateMode === "LIVE") return "text-green-600 bg-green-50";
    if (branch.rateMode === "INHERIT") return "text-purple-600 bg-purple-50";
    return "text-blue-600 bg-blue-50";
  };

  const renderBranch = (branch) => (
    <div
      key={branch.code}
      className={`flex items-center p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${getBranchColor(
        branch
      )}`}
      onClick={() => onSelectBranch(branch)}
    >
      <OfficeBuildingIcon className="h-5 w-5 mr-2" />
      <div className="flex-1">
        <div className="flex items-center">
          <span className="font-medium">{branch.name}</span>
          {branch.code === mainBranch && (
            <StarIcon className="h-4 w-4 ml-2 text-yellow-500" />
          )}
        </div>
        <div className="text-xs opacity-75">
          {branch.code} • {branch.rateMode || "ADMIN"}
        </div>
      </div>

      {branch.stores && branch.stores.length > 0 && (
        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
          {branch.stores.length} stores
        </span>
      )}
    </div>
  );

  if (loadingList) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-sm text-gray-500 mt-2">Loading branches...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {Object.entries(organizedBranches).map(([state, stateBranches]) => (
        <div key={state} className="border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleExpand(state)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center">
              {expandedNodes[state] ? (
                <ChevronDownIcon className="h-5 w-5 mr-2 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 mr-2 text-gray-500" />
              )}
              <span className="font-semibold">{state}</span>
            </div>
            <span className="text-sm text-gray-500">
              {stateBranches.length} branch
              {stateBranches.length !== 1 ? "es" : ""}
            </span>
          </button>

          {expandedNodes[state] && (
            <div className="p-2 space-y-1">
              {stateBranches.map(renderBranch)}
            </div>
          )}
        </div>
      ))}

      {branches.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No branches configured
        </div>
      )}

      {mainBranch && onSetMain && (
        <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Main Branch:</strong> {mainBranch}
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            Other branches can inherit rates from the main branch
          </p>
        </div>
      )}
    </div>
  );
};

export default BranchHierarchyTree;
